'use client';

import { c as _c } from "react/compiler-runtime";
import type { VaultV2BlueReallocation } from '@morpho-org/morpho-sdk';
import { type MarketParams, MathLib } from '@morpho-org/blue-sdk';
import { getIsPositionUnhealthy, type InterfaceToken, type MarketForRewards, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Button, Icon } from '@repo/ui';
import { Alert, Close } from '@repo/ui/icons';
import { useMemo, useState } from 'react';
import { useWalletClient } from 'wagmi';
import type { CheckoutEntryPoint } from '@/lib/analytics/client/events';
import type { TxSourceFlow } from '@/lib/shared/utils/flags.constants';
import MarketRecapV2 from '@/components/Bundler/ConfirmDialog/Recaps/MarketRecapV2';
import { useProtectedLltv } from '@/components/common/AutoDeleverage/ProtectedLltv';
import CloseToLiquidationConfirmBox from '@/components/common/CloseToLiquidationConfirmBox/CloseToLiquidationConfirmBox';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getRequirementsErrorMessage } from '@/helpers/requirementErrors';
import { computeLtv, type MarketActionKind, submitUnlessDeadlineExpired } from '@/hooks/operation/market/v2/marketAction.helpers';
import { getMarketActionAlertMessage } from '@/hooks/operation/market/v2/marketActionError';
import { useMarketActionBuilder } from '@/hooks/operation/market/v2/useMarketActionBuilder';
import { useMarketSimulatedFinalPosition } from '@/hooks/operation/market/v2/useMarketSimulatedFinalPosition';
import useCloseDialogOnWalletChange from '@/hooks/useCloseDialogOnWalletChange';
import { useReviewConfirmGate } from '@/hooks/useDeferredSubmit';
import { useReviewFunnelTracking } from '@/lib/analytics/client/hooks/useReviewFunnelTracking';
import { buildReviewDialogParams } from '@/lib/analytics/client/trackers/checkout-funnel';
import { getSdkTracking } from '@/lib/observability/client/trackers/transaction/sdk';
import { logger } from '@/lib/observability/logger';
import { useLogSdkBuildFailed } from '@/lib/observability/sdk';
import { InterfaceOperation } from '@/operations/operations.types';
import { tryBuildSimulationAuthorizations } from '@/services/simulate/simulatedPosition.helpers';

/** Map the builder's resolved action kind to the analytics operation type (parity with Vault V2). */
const KIND_TO_OPERATION_TYPE: Partial<Record<MarketActionKind, InterfaceOperation.InterfaceOperationType>> = {
  supplyCollateral: InterfaceOperation.InterfaceOperationType.supplyCollateral,
  borrow: InterfaceOperation.InterfaceOperationType.borrow,
  supplyCollateralBorrow: InterfaceOperation.InterfaceOperationType.supplyCollateralBorrow,
  repay: InterfaceOperation.InterfaceOperationType.repay,
  withdrawCollateral: InterfaceOperation.InterfaceOperationType.withdrawCollateral,
  repayWithdrawCollateral: InterfaceOperation.InterfaceOperationType.withdrawCollateralRepay
};
const KIND_TO_TX_FLOW = {
  supply: undefined,
  supplyCollateral: 'marketSupplyCollateral',
  borrow: 'marketBorrow',
  supplyCollateralBorrow: 'marketSupplyCollateralBorrow',
  repay: 'marketRepay',
  withdrawCollateral: 'marketWithdrawCollateral',
  repayWithdrawCollateral: 'marketRepayWithdrawCollateral',
  withdraw: undefined,
  none: undefined
} satisfies Record<MarketActionKind, TxSourceFlow | undefined>;

/** Review CTA verb per resolved action kind; combined actions keep the generic label. */
const KIND_TO_CTA_LABEL: Partial<Record<MarketActionKind, string>> = {
  supply: 'Supply',
  supplyCollateral: 'Supply',
  borrow: 'Borrow',
  repay: 'Repay',
  withdrawCollateral: 'Withdraw',
  withdraw: 'Withdraw'
};
const getReviewBuildError = (buildError: unknown, simulationApprovalError: unknown): unknown => buildError ?? simulationApprovalError;
export interface MarketActionReviewDialogV2Props {
  marketParams: MarketParams;
  marketId: string;
  mode: 'supplyBorrow' | 'repayWithdraw';
  collateralAmount: bigint;
  loanAmount: bigint;
  nativeAmount?: bigint;
  isMaxRepay?: boolean;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  /** Market liquidation LTV (WAD). */
  lltv: bigint;
  /**
   * Public-allocator reallocations (WS6) sized for `loanAmount`, computed at the
   * entry point. Threaded into the borrow / supplyCollateralBorrow build; their
   * native-ETH fee accumulates in `tx.value` and is surfaced below the recap.
   */
  reallocations?: readonly VaultV2BlueReallocation[];
  /** APY + rewards source for the recap's Rate row (same data as the panel summary card). */
  marketForRewards?: MarketForRewards | null;
  avgBorrowApy?: number | null;
  /** Optional post-success hook (e.g. reset inputs / fire conversion analytics). */
  onTxSuccess?: () => void;
  /** Where the review was opened from — drives the checkout-funnel `entry_point` dimension. */
  entryPoint: CheckoutEntryPoint;
}

/**
 * Close-to-liquidation checkbox verdict, keyed off the settled simulation (EVM sim, or JS-math
 * fallback when it fails). Frozen while the sim is loading so the verdict keeps its last settled
 * value — the first load shows nothing, and later reloads don't move it until a result settles.
 * Derived during render (not in an effect) so the same commit that settles the sim also gates
 * Confirm on it — a parked click replayed in that commit must see the fresh verdict.
 */
const useSettledRiskVerdict = t0 => {
  const $ = _c(3);
  const {
    isSimLoading,
    ltvAfter,
    protectedLltv
  } = t0;
  const [settledRisk, setSettledRisk] = useState(false);
  let t1;
  if ($[0] !== ltvAfter || $[1] !== protectedLltv) {
    t1 = isDefined(ltvAfter) && isDefined(protectedLltv) && getIsPositionUnhealthy(ltvAfter, protectedLltv);
    $[0] = ltvAfter;
    $[1] = protectedLltv;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const riskNow = t1;
  if (!isSimLoading && riskNow !== settledRisk) {
    setSettledRisk(riskNow);
  }
  return isSimLoading ? settledRisk : riskNow;
};

/**
 * Review + execution for a market-actions-v2 action (WS4 assembly): wires
 * `useMarketActionBuilder` → `useMarketSimulatedFinalPosition` → `MarketRecapV2`
 * → `submitTransactions` + `ConfirmDialogV2`, the market sister of
 * `VaultV2DepositReviewDialog`. Reached only when the entry point's flow flag
 * (`marketSupplyBorrow` / `marketRepayWithdraw`) is on — there is
 * no legacy fallback; when off the action button is disabled.
 */
const MarketActionReviewDialogV2 = t0 => {
  const $ = _c(146);
  const {
    marketParams,
    marketId,
    mode,
    collateralAmount,
    loanAmount,
    nativeAmount: t1,
    isMaxRepay: t2,
    collateralAsset,
    loanAsset,
    lltv,
    reallocations,
    marketForRewards,
    avgBorrowApy,
    onTxSuccess,
    entryPoint
  } = t0;
  const nativeAmount = t1 === undefined ? ZERO : t1;
  const isMaxRepay = t2 === undefined ? false : t2;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    data: client
  } = useWalletClient();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    account,
    targetChain,
    targetChainId
  } = useWeb3Context();
  const [isRiskAcknowledged, setIsRiskAcknowledged] = useState(false);
  const [reviewSessionId] = useState(_temp);
  useCloseDialogOnWalletChange();
  let t3;
  if ($[0] !== collateralAmount || $[1] !== isMaxRepay || $[2] !== loanAmount || $[3] !== marketParams || $[4] !== mode || $[5] !== nativeAmount || $[6] !== reallocations) {
    t3 = {
      marketParams,
      mode,
      collateralAmount,
      loanAmount,
      nativeAmount,
      isMaxRepay,
      reallocations
    };
    $[0] = collateralAmount;
    $[1] = isMaxRepay;
    $[2] = loanAmount;
    $[3] = marketParams;
    $[4] = mode;
    $[5] = nativeAmount;
    $[6] = reallocations;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    transaction,
    requirements,
    areRequirementsReady,
    isRequirementsPlaceholderData,
    kind,
    positionData,
    simulationKey: builtSimulationKey,
    marketError,
    buildError,
    deadline,
    rebuild,
    isRefreshing: isBundleRefreshing,
    isLoading
  } = useMarketActionBuilder(t3);
  const approvalAsset = mode === "supplyBorrow" ? collateralAsset : loanAsset;
  let t4;
  if ($[8] !== account.address || $[9] !== requirements || $[10] !== targetChainId) {
    t4 = tryBuildSimulationAuthorizations({
      requirements,
      chainId: targetChainId,
      from: account.address
    });
    $[8] = account.address;
    $[9] = requirements;
    $[10] = targetChainId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    simulationAuthorizations,
    simulationApprovalError
  } = t4;
  let t5;
  if ($[12] !== buildError || $[13] !== simulationApprovalError) {
    t5 = getReviewBuildError(buildError, simulationApprovalError);
    $[12] = buildError;
    $[13] = simulationApprovalError;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== kind || $[16] !== reviewSessionId || $[17] !== t5 || $[18] !== targetChainId) {
    t6 = {
      sdkAction: kind,
      chainId: targetChainId,
      reviewSessionId,
      error: t5
    };
    $[15] = kind;
    $[16] = reviewSessionId;
    $[17] = t5;
    $[18] = targetChainId;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  useLogSdkBuildFailed(t6);
  let t7;
  if ($[20] !== positionData) {
    t7 = positionData ? {
      collateral: positionData.collateral,
      borrowAssets: positionData.borrowAssets,
      price: positionData.market.price ?? ZERO
    } : undefined;
    $[20] = positionData;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const current = t7;
  let t8;
  bb0: {
    if (!current) {
      t8 = undefined;
      break bb0;
    }
    if (mode === "supplyBorrow") {
      const t9 = current.collateral + collateralAmount;
      const t10 = current.borrowAssets + loanAmount;
      let t11;
      if ($[22] !== t10 || $[23] !== t9) {
        t11 = {
          collateral: t9,
          borrowAssets: t10
        };
        $[22] = t10;
        $[23] = t9;
        $[24] = t11;
      } else {
        t11 = $[24];
      }
      t8 = t11;
      break bb0;
    }
    let t9;
    if ($[25] !== collateralAmount || $[26] !== current.collateral) {
      t9 = MathLib.zeroFloorSub(current.collateral, collateralAmount);
      $[25] = collateralAmount;
      $[26] = current.collateral;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    let t10;
    if ($[28] !== current.borrowAssets || $[29] !== loanAmount) {
      t10 = MathLib.zeroFloorSub(current.borrowAssets, loanAmount);
      $[28] = current.borrowAssets;
      $[29] = loanAmount;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    let t11;
    if ($[31] !== t10 || $[32] !== t9) {
      t11 = {
        collateral: t9,
        borrowAssets: t10
      };
      $[31] = t10;
      $[32] = t9;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    t8 = t11;
  }
  const fallback = t8;
  let t9;
  if ($[34] !== builtSimulationKey) {
    t9 = builtSimulationKey ? [builtSimulationKey] : undefined;
    $[34] = builtSimulationKey;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const simulationKey = t9;
  let t10;
  if ($[36] !== areRequirementsReady || $[37] !== current || $[38] !== fallback || $[39] !== isLoading || $[40] !== marketId || $[41] !== marketParams || $[42] !== simulationAuthorizations || $[43] !== simulationKey || $[44] !== targetChainId || $[45] !== transaction) {
    t10 = {
      chainId: targetChainId,
      marketId,
      marketParams,
      transaction,
      simulationAuthorizations,
      simulationKey,
      isPreparing: isLoading,
      areRequirementsReady,
      current,
      fallback
    };
    $[36] = areRequirementsReady;
    $[37] = current;
    $[38] = fallback;
    $[39] = isLoading;
    $[40] = marketId;
    $[41] = marketParams;
    $[42] = simulationAuthorizations;
    $[43] = simulationKey;
    $[44] = targetChainId;
    $[45] = transaction;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  const {
    collateralAfter,
    borrowAfter,
    ltvAfter,
    isUnverifiedFinalPosition,
    status: simulationStatus,
    isLoading: isSimLoading,
    isRefreshing: isSimRefreshing
  } = useMarketSimulatedFinalPosition(t10);
  let t11;
  if ($[47] !== marketParams.id || $[48] !== marketParams.lltv || $[49] !== targetChainId) {
    t11 = {
      lltv: marketParams.lltv,
      marketId: marketParams.id,
      chainId: targetChainId
    };
    $[47] = marketParams.id;
    $[48] = marketParams.lltv;
    $[49] = targetChainId;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  const protectedLltv = useProtectedLltv(t11);
  let t12;
  if ($[51] !== isSimLoading || $[52] !== ltvAfter || $[53] !== protectedLltv) {
    t12 = {
      isSimLoading,
      ltvAfter,
      protectedLltv
    };
    $[51] = isSimLoading;
    $[52] = ltvAfter;
    $[53] = protectedLltv;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  const isFinalPositionRisky = useSettledRiskVerdict(t12);
  const isSimulationBlacklisted = simulationStatus === "blacklist";
  const isHardBlocked = !client || !!buildError || !!marketError || !account.isWalletTypeResolved || account.isWrongChain || isSimulationBlacklisted || simulationApprovalError !== undefined || isFinalPositionRisky && !isRiskAcknowledged;
  const isPrepared = !!transaction && areRequirementsReady && !isLoading && !isSimLoading;
  const t13 = current?.collateral ?? ZERO;
  const t14 = current?.borrowAssets ?? ZERO;
  const t15 = current?.collateral ?? ZERO;
  const t16 = current?.borrowAssets ?? ZERO;
  const t17 = current?.price ?? ZERO;
  let t18;
  if ($[55] !== t15 || $[56] !== t16 || $[57] !== t17) {
    t18 = computeLtv({
      collateral: t15,
      borrowAssets: t16,
      price: t17
    });
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t13 || $[60] !== t14 || $[61] !== t18) {
    t19 = {
      collateral: t13,
      borrowAssets: t14,
      ltv: t18
    };
    $[59] = t13;
    $[60] = t14;
    $[61] = t18;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  const initial = t19;
  const t20 = collateralAmount > ZERO ? collateralAfter : initial.collateral;
  const t21 = loanAmount > ZERO ? borrowAfter : initial.borrowAssets;
  let t22;
  if ($[63] !== ltvAfter || $[64] !== t20 || $[65] !== t21) {
    t22 = {
      collateral: t20,
      borrowAssets: t21,
      ltv: ltvAfter
    };
    $[63] = ltvAfter;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  const final = t22;
  const t23 = !current;
  const t24 = isSimLoading || !current;
  let t25;
  if ($[67] !== avgBorrowApy || $[68] !== collateralAmount || $[69] !== collateralAsset || $[70] !== final || $[71] !== initial || $[72] !== isUnverifiedFinalPosition || $[73] !== lltv || $[74] !== loanAmount || $[75] !== loanAsset || $[76] !== marketForRewards || $[77] !== marketParams.id || $[78] !== mode || $[79] !== t23 || $[80] !== t24 || $[81] !== targetChainId) {
    t25 = <MarketRecapV2 marketId={marketParams.id} chainId={targetChainId} collateralAsset={collateralAsset} loanAsset={loanAsset} mode={mode} collateralAmount={collateralAmount} loanAmount={loanAmount} initial={initial} final={final} lltv={lltv} marketForRewards={marketForRewards} avgBorrowApy={avgBorrowApy} isInitialPositionLoading={t23} isFinalPositionLoading={t24} isSimulationFallback={isUnverifiedFinalPosition} />;
    $[67] = avgBorrowApy;
    $[68] = collateralAmount;
    $[69] = collateralAsset;
    $[70] = final;
    $[71] = initial;
    $[72] = isUnverifiedFinalPosition;
    $[73] = lltv;
    $[74] = loanAmount;
    $[75] = loanAsset;
    $[76] = marketForRewards;
    $[77] = marketParams.id;
    $[78] = mode;
    $[79] = t23;
    $[80] = t24;
    $[81] = targetChainId;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  const recap = t25;
  const operationType = KIND_TO_OPERATION_TYPE[kind];
  let t26;
  if ($[83] !== account.isSafe || $[84] !== entryPoint || $[85] !== operationType || $[86] !== requirements || $[87] !== reviewSessionId) {
    t26 = buildReviewDialogParams({
      reviewSessionId,
      entryPoint,
      operationTypes: operationType ? [operationType] : [],
      isSafe: account.isSafe,
      requirements
    });
    $[83] = account.isSafe;
    $[84] = entryPoint;
    $[85] = operationType;
    $[86] = requirements;
    $[87] = reviewSessionId;
    $[88] = t26;
  } else {
    t26 = $[88];
  }
  const reviewTrackingParams = t26;
  const {
    trackClose,
    confirmAttempt
  } = useReviewFunnelTracking(reviewTrackingParams, !isLoading);
  let t27;
  if ($[89] !== toggleDialog || $[90] !== trackClose) {
    t27 = () => {
      trackClose();
      toggleDialog(false);
    };
    $[89] = toggleDialog;
    $[90] = trackClose;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  const handleClose = t27;
  let t28;
  if ($[92] !== account || $[93] !== approvalAsset || $[94] !== areRequirementsReady || $[95] !== client || $[96] !== confirmAttempt || $[97] !== deadline || $[98] !== isHardBlocked || $[99] !== kind || $[100] !== onTxSuccess || $[101] !== rebuild || $[102] !== recap || $[103] !== requirements || $[104] !== submitTransactions || $[105] !== targetChain || $[106] !== toggleDialog || $[107] !== transaction) {
    t28 = () => {
      if (!transaction || !client || !areRequirementsReady || isHardBlocked) {
        return;
      }
      submitUnlessDeadlineExpired({
        deadline,
        rebuild,
        submit: () => {
          const checkoutTracking = confirmAttempt();
          submitTransactions(client, {
            actionType: kind,
            flow: KIND_TO_TX_FLOW[kind],
            transaction,
            ...getSdkTracking(transaction),
            requirements,
            onTxSuccess,
            chain: targetChain,
            account,
            checkoutTracking
          }).catch(error => {
            logger.error(error instanceof Error ? error : new Error(String(error)), {
              tags: {
                component: "MarketActionReviewDialogV2"
              }
            });
            toggleDialog(false);
          });
          toggleDialog(<ConfirmDialogV2 txMessage="Execute your bundle" token={approvalAsset} operationContent={recap} />, {
            closable: false
          });
        }
      });
    };
    $[92] = account;
    $[93] = approvalAsset;
    $[94] = areRequirementsReady;
    $[95] = client;
    $[96] = confirmAttempt;
    $[97] = deadline;
    $[98] = isHardBlocked;
    $[99] = kind;
    $[100] = onTxSuccess;
    $[101] = rebuild;
    $[102] = recap;
    $[103] = requirements;
    $[104] = submitTransactions;
    $[105] = targetChain;
    $[106] = toggleDialog;
    $[107] = transaction;
    $[108] = t28;
  } else {
    t28 = $[108];
  }
  const handleConfirm = t28;
  const t29 = isRequirementsPlaceholderData || isBundleRefreshing || isSimRefreshing;
  let t30;
  if ($[109] !== handleConfirm || $[110] !== isHardBlocked || $[111] !== isPrepared || $[112] !== t29) {
    t30 = {
      isHardBlocked,
      isPrepared,
      isRefreshSignal: t29,
      submit: handleConfirm
    };
    $[109] = handleConfirm;
    $[110] = isHardBlocked;
    $[111] = isPrepared;
    $[112] = t29;
    $[113] = t30;
  } else {
    t30 = $[113];
  }
  const {
    request: requestConfirm,
    isDisabled: isConfirmDisabled,
    isRefreshing: isConfirmRefreshing
  } = useReviewConfirmGate(t30);
  let t31;
  if ($[114] !== buildError || $[115] !== marketError) {
    t31 = getMarketActionAlertMessage({
      buildError,
      marketError
    });
    $[114] = buildError;
    $[115] = marketError;
    $[116] = t31;
  } else {
    t31 = $[116];
  }
  const alertMessage = t31;
  let t32;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <span className="text-title-sm">Review</span>;
    $[117] = t32;
  } else {
    t32 = $[117];
  }
  let t33;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Close />;
    $[118] = t33;
  } else {
    t33 = $[118];
  }
  let t34;
  if ($[119] !== handleClose) {
    t34 = <div className="flex items-center justify-between gap-[16px]">{t32}<Button size="icon" onClick={handleClose} variant="secondary" aria-label="Close">{t33}</Button></div>;
    $[119] = handleClose;
    $[120] = t34;
  } else {
    t34 = $[120];
  }
  let t35;
  if ($[121] !== alertMessage || $[122] !== isLoading) {
    t35 = !isLoading && alertMessage && <div className="gap-2xs flex flex-row items-center" role="alert"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">{alertMessage}</span></div>;
    $[121] = alertMessage;
    $[122] = isLoading;
    $[123] = t35;
  } else {
    t35 = $[123];
  }
  let t36;
  if ($[124] !== simulationApprovalError) {
    t36 = simulationApprovalError && <div className="gap-2xs flex flex-row items-center" role="alert"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">{getRequirementsErrorMessage(simulationApprovalError)}</span></div>;
    $[124] = simulationApprovalError;
    $[125] = t36;
  } else {
    t36 = $[125];
  }
  let t37;
  if ($[126] !== isSimulationBlacklisted) {
    t37 = isSimulationBlacklisted && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="market-action-v2-blacklist-error"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">This transaction can't be completed due to a simulation error.</span></div>;
    $[126] = isSimulationBlacklisted;
    $[127] = t37;
  } else {
    t37 = $[127];
  }
  let t38;
  if ($[128] !== isFinalPositionRisky || $[129] !== isRiskAcknowledged) {
    t38 = isFinalPositionRisky && <CloseToLiquidationConfirmBox isRiskyPositionsAcknowledged={isRiskAcknowledged} setIsRiskyPositionsAcknowledged={setIsRiskAcknowledged} />;
    $[128] = isFinalPositionRisky;
    $[129] = isRiskAcknowledged;
    $[130] = t38;
  } else {
    t38 = $[130];
  }
  let t39;
  if ($[131] !== requestConfirm) {
    t39 = () => requestConfirm();
    $[131] = requestConfirm;
    $[132] = t39;
  } else {
    t39 = $[132];
  }
  const t40 = KIND_TO_CTA_LABEL[kind] ?? "Confirm";
  let t41;
  if ($[133] !== isConfirmDisabled || $[134] !== isConfirmRefreshing || $[135] !== t39 || $[136] !== t40) {
    t41 = <Button data-testid="confirm-button" data-refreshing={isConfirmRefreshing} size="lg" disabled={isConfirmDisabled} onClick={t39}>{t40}</Button>;
    $[133] = isConfirmDisabled;
    $[134] = isConfirmRefreshing;
    $[135] = t39;
    $[136] = t40;
    $[137] = t41;
  } else {
    t41 = $[137];
  }
  let t42;
  if ($[138] !== recap || $[139] !== t34 || $[140] !== t35 || $[141] !== t36 || $[142] !== t37 || $[143] !== t38 || $[144] !== t41) {
    t42 = <div className="gap-lg flex flex-col" data-testid="confirm-dialog">{t34}{recap}{t35}{t36}{t37}{t38}{t41}</div>;
    $[138] = recap;
    $[139] = t34;
    $[140] = t35;
    $[141] = t36;
    $[142] = t37;
    $[143] = t38;
    $[144] = t41;
    $[145] = t42;
  } else {
    t42 = $[145];
  }
  return t42;
};
export default MarketActionReviewDialogV2;
function _temp() {
  return crypto.randomUUID();
}